import React, { useState, useCallback, useMemo } from 'react';
import { Container, Row, Col, Form, Button, InputGroup, FormControl, Badge, Alert } from 'react-bootstrap';
import {
  FaCalendarAlt, FaGraduationCap, FaBook, FaClock, FaSearch, 
  FaUniversity, FaChartBar, FaClipboardList, FaEye, FaEdit,
  FaUserGraduate, FaBookOpen, FaCertificate, FaSchool,
  FaCalendarCheck, FaFileAlt, FaGlobe, FaUsers, FaAward
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import Cookies from 'js-cookie';
import axios from '../api/axios';
import GenericCrudPage from '../components/GenericCrudPage';
import GenericViewModal from '../components/GenericViewModal';
import {
  useDegrees,
  usePrograms,
  useProgramYears,
  useAcademicYears,
  useSemesters,
} from '../hooks/useDropdownData';

const BASE_URL = process.env.REACT_APP_BASE_URL;

// Utility functions for date conversion
const formatDateForAPI = (dateString) => {
  if (!dateString) return "";
  // If it's already in DD/MM/YYYY format, return as is
  if (dateString.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) return dateString;
  // If it's in YYYY-MM-DD format (from date input), convert to DD/MM/YYYY
  if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
    const [year, month, day] = dateString.split("-");
    return `${parseInt(day)}/${parseInt(month)}/${year}`;
  }
  return dateString;
};

const convertToInputDateFormat = (dateStr) => {
  if (!dateStr) return "";
  // If already in YYYY-MM-DD format, return as is
  if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) return dateStr;
  // If in DD/MM/YYYY format, convert to YYYY-MM-DD
  if (dateStr.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) {
    const parts = dateStr.split("/");
    if (parts.length === 3) {
      const [day, month, year] = parts;
      if (day && month && year && year.length === 4) {
        return `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
      }
    }
  }
  return "";
};

const formatDateForDisplay = (dateStr) => {
  if (!dateStr) return "";
  try {
    // If already in DD/MM/YYYY format
    if (dateStr.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) return dateStr;
    
    // If in YYYY-MM-DD format
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateStr.split("-");
      return `${parseInt(day)}/${parseInt(month)}/${year}`;
    }
    
    // For ISO date strings
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "";
    return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
  } catch {
    return "";
  }
};

// Exam status options
const EXAM_STATUS_OPTIONS = [
  { value: true, label: 'Live (Published)' },
  { value: false, label: 'Draft (Unpublished)' }
];

const Examination = () => {
  const [filterDegree, setFilterDegree] = useState("");
  const [searchExamName, setSearchExamName] = useState("");
  const [showExamViewModal, setShowExamViewModal] = useState(false);
  const [selectedExam, setSelectedExam] = useState(null);
  const [examStats, setExamStats] = useState({ total: 0, live: 0, draft: 0 });

  // Load dropdown data
  const { degrees, loading: degreesLoading } = useDegrees();
  const { programs, loading: programsLoading } = usePrograms();
  const { programYears, loading: programYearsLoading } = useProgramYears();
  const { academicYears, loading: academicYearsLoading } = useAcademicYears();
  const { semesters, loading: semestersLoading } = useSemesters();

  const dropdownsLoading = degreesLoading || programsLoading || programYearsLoading || 
                           academicYearsLoading || semestersLoading;

  // Memoized custom fetch function to prevent unnecessary re-renders
// Corrected custom fetch function
const customFetch = useCallback(async () => {
    const token = Cookies.get('token');
    let url = `${BASE_URL}/academic/getAllExamAnnouncement`;

    if (filterDegree) {
        url += `?degree=${filterDegree}`;
    }

    try {
        const res = await axios.get(url, {
            headers: { Authorization: `Bearer ${token}` }
        });

        const rawExamList = res.data?.examannouncement || [];

        // **FIX: Transform the API response to match what the component expects**
        const transformedExamList = rawExamList.map(exam => ({
            ...exam, // Keep all original properties from the API response
            // Map the API's ID keys (e.g., DegreeId) to the keys your component uses (e.g., degree)
            degree: exam.DegreeId,
            programmes: exam.programmeId,
            programmeyear: exam.programmeyearId,
            academicyear: exam.academicyearId,
            semester: exam.semesterId,
        }));
        
        // Calculate statistics using the transformed list
        const stats = {
            total: transformedExamList.length,
            // Handle both boolean (true/false) and number (1/0) values for robustness
            live: transformedExamList.filter(exam => exam.exIsLive === 1 || exam.exIsLive === true).length,
            draft: transformedExamList.filter(exam => exam.exIsLive === 0 || exam.exIsLive === false).length
        };
        setExamStats(stats);

        // Return the newly transformed list
        return transformedExamList;

    } catch (error) {
        console.error("Failed to fetch exams:", error);
        toast.error("Could not fetch exam data.");
        return [];
    }
}, [filterDegree]); // Dependency remains the same


  // Fetch single exam for view/edit
  const fetchExamById = useCallback(async (item) => {
    if (!item || !item.id) {
      toast.error("Invalid exam selected.");
      return null;
    }
    
    // For exams, we can return the item as is since it already contains all needed data
    // If you need additional data, you can make an API call here
    return item;
  }, []);

  // Enhanced form validation
  const validateForm = useCallback((form) => {
    const errors = {};
    
    // Required fields differ for create vs update
    const isEdit = !!form.id;
    const requiredFields = isEdit 
      ? ['exName'] // Only exam name required for updates
      : ['exName', 'degree', 'programmes', 'programmeyear', 'academicyear', 'semester']; // All academic fields required for create
    
    // Check required fields
    requiredFields.forEach(field => {
      if (!form[field] || form[field] === "") {
        errors[field] = `${field.replace('ex', '').replace(/([A-Z])/g, ' $1').trim()} is required`;
      }
    });

    // Validate exam name length
    if (form.exName && form.exName.length < 3) {
      errors.exName = "Exam name must be at least 3 characters long";
    }

    // Validate date ranges
    const dateFields = [
      ['exExamDetailsStartDate', 'exExamDetailsEndDate', 'Exam'],
      ['exPracticalExamStartDate', 'exPracticalExamEndDate', 'Practical Exam'],
      ['exSemesterBreakStartDate', 'exSemesterBreakEndDate', 'Semester Break'],
      ['exSemesterDetailsStartDate', 'exSemesterDetailsEndDate', 'Semester']
    ];

    dateFields.forEach(([startField, endField, fieldName]) => {
      const startDate = form[startField];
      const endDate = form[endField];
      
      if (startDate && endDate) {
        const start = new Date(startDate);
        const end = new Date(endDate);
        
        if (start > end) {
          errors[startField] = `${fieldName} start date cannot be after end date`;
          errors[endField] = `${fieldName} end date cannot be before start date`;
        }
      }
    });

    return errors;
  }, []);

  // Memoized configuration for CRUD operations
  const config = useMemo(() => ({
    baseUrl: BASE_URL,
    endpoints: {
      fetch: '/academic/getAllExamAnnouncement',
      save: '/academic/createExamAnnouncement',
      update: '/academic/updateExamAnnouncement',
      delete: '/academic/deleteExamAnnouncement'
    },
    validateForm: validateForm,
    initialFormState: {
      exName: "",
      exLocalLangName: "",
      exDescription: "",
      exExaminationPeriod: "",
      exInstructionalPeriod: "",
      exPracticalPeriod: "",
      exExamDetailsStartDate: "",
      exExamDetailsEndDate: "",
      exPracticalExamStartDate: "",
      exPracticalExamEndDate: "",
      exSemesterBreakStartDate: "",
      exSemesterBreakEndDate: "",
      exSemesterDetailsStartDate: "",
      exSemesterDetailsEndDate: "",
      degree: "",
      programmes: "",
      programmeyear: "",
      academicyear: "",
      semester: "",
      exIsLive: false,
      exIsActive: 1,
      user: Number(Cookies.get("userId") || 1),
    },
    entityName: 'Exam',
    fetchMethod: 'CUSTOM',
    deleteMethod: 'GET',
    customFetch: customFetch,
  }), [validateForm, customFetch]);

  // Transform form data for API submission
  const transformPayload = useCallback((form, editId, userId) => {
    const payload = {
      exName: form.exName?.trim() || "",
      exLocalLangName: form.exLocalLangName?.trim() || "",
      exDescription: form.exDescription?.trim() || "",
      exExaminationPeriod: form.exExaminationPeriod?.trim() || "",
      exInstructionalPeriod: form.exInstructionalPeriod?.trim() || "",
      exPracticalPeriod: form.exPracticalPeriod?.trim() || "",
      exIsLive: form.exIsLive ? 1 : 0,
      exIsActive: 1,
      user: Number(userId) || 1,
    };

    // Add academic fields only for create operations
    if (!editId) {
      payload.degree = Number(form.degree);
      payload.programmes = Number(form.programmes);
      payload.programmeyear = Number(form.programmeyear);
      payload.academicyear = Number(form.academicyear);
      payload.semester = Number(form.semester);
    }

    // Add dates in API format (DD/MM/YYYY) only if they exist
    const dateFields = [
      'exExamDetailsStartDate', 'exExamDetailsEndDate',
      'exPracticalExamStartDate', 'exPracticalExamEndDate',
      'exSemesterBreakStartDate', 'exSemesterBreakEndDate',
      'exSemesterDetailsStartDate', 'exSemesterDetailsEndDate'
    ];

    dateFields.forEach(field => {
      if (form[field]) {
        payload[field] = formatDateForAPI(form[field]);
      }
    });

    // Add ID for update
    if (editId) {
      payload.id = editId;
    }

    // Remove empty values
    Object.keys(payload).forEach(key => {
      if (payload[key] === "" || payload[key] === null || payload[key] === undefined) {
        delete payload[key];
      }
    });

    return payload;
  }, []);

  // Transform API data to form state for editing
  const transformToForm = useCallback((item) => {
    if (!item) return config.initialFormState;

    return {
      ...config.initialFormState,
      ...item,
      degree: item.degree?.toString() || "",
      programmes: item.programmes?.toString() || "",
      programmeyear: item.programmeyear?.toString() || "",
      academicyear: item.academicyear?.toString() || "",
      semester: item.semester?.toString() || "",
      exExamDetailsStartDate: convertToInputDateFormat(item.exExamDetailsStartDate),
      exExamDetailsEndDate: convertToInputDateFormat(item.exExamDetailsEndDate),
      exPracticalExamStartDate: convertToInputDateFormat(item.exPracticalExamStartDate),
      exPracticalExamEndDate: convertToInputDateFormat(item.exPracticalExamEndDate),
      exSemesterBreakStartDate: convertToInputDateFormat(item.exSemesterBreakStartDate),
      exSemesterBreakEndDate: convertToInputDateFormat(item.exSemesterBreakEndDate),
      exSemesterDetailsStartDate: convertToInputDateFormat(item.exSemesterDetailsStartDate),
      exSemesterDetailsEndDate: convertToInputDateFormat(item.exSemesterDetailsEndDate),
      exIsLive: item.exIsLive === 1,
      exIsActive: item.exIsActive !== undefined ? item.exIsActive : 1,
    };
  }, [config.initialFormState]);

  // Memoized form fields to prevent re-renders
  const formFields = useMemo(() => [
    {
      title: '📚 Basic Information',
      fields: [
        { name: 'exName', label: 'Exam Name', icon: FaClipboardList, required: true, placeholder: 'Enter exam name' },
        { name: 'exLocalLangName', label: 'Local Language Name', icon: FaGlobe, placeholder: 'Enter local language name' },
        { name: 'exDescription', label: 'Description', icon: FaFileAlt, as: 'textarea', rows: 3, placeholder: 'Enter exam description' },
        { name: 'exIsLive', label: 'Publication Status', icon: FaEye, as: 'switch', 
          helpText: 'Toggle to publish/unpublish the exam announcement' },
      ]
    },
    {
      title: '🎓 Academic Information',
      fields: [
        { name: 'degree', label: 'Degree', icon: FaGraduationCap, required: true, as: 'select', 
          options: (Array.isArray(degrees) ? degrees : []).filter(Boolean).map(d => ({ value: d.id, label: d.exName })),
          hideOnEdit: true, helpText: 'Cannot be changed after creation' },
        { name: 'programmes', label: 'Program', icon: FaBookOpen, required: true, as: 'select',
          options: (Array.isArray(programs) ? programs : []).filter(Boolean).map(p => ({ value: p.id, label: p.exName })),
          hideOnEdit: true },
        { name: 'programmeyear', label: 'Program Year', icon: FaSchool, required: true, as: 'select',
          options: (Array.isArray(programYears) ? programYears : []).filter(Boolean).map(py => ({ value: py.id, label: py.exName })),
          hideOnEdit: true },
        { name: 'academicyear', label: 'Academic Year', icon: FaCalendarCheck, required: true, as: 'select',
          options: (Array.isArray(academicYears) ? academicYears : []).filter(Boolean).map(ay => ({ value: ay.id, label: ay.exYear })),
          hideOnEdit: true },
        { name: 'semester', label: 'Semester', icon: FaBook, required: true, as: 'select',
          options: (Array.isArray(semesters) ? semesters : []).filter(Boolean).map(s => ({ value: s.id, label: s.exName })),
          hideOnEdit: true },
      ]
    },
    {
      title: '⏰ Exam Configuration',
      fields: [
        { name: 'exExaminationPeriod', label: 'Examination Period', icon: FaClock, placeholder: 'e.g., 3 hours' },
        { name: 'exInstructionalPeriod', label: 'Instructional Period', icon: FaClock, placeholder: 'e.g., 10 days' },
        { name: 'exPracticalPeriod', label: 'Practical Period', icon: FaClock, placeholder: 'e.g., 5 days' },
      ]
    },
    {
      title: '📅 Examination Dates',
      fields: [
        { name: 'exExamDetailsStartDate', label: 'Exam Start Date', icon: FaCalendarAlt, type: 'date' },
        { name: 'exExamDetailsEndDate', label: 'Exam End Date', icon: FaCalendarAlt, type: 'date' },
        { name: 'exPracticalExamStartDate', label: 'Practical Exam Start Date', icon: FaCalendarAlt, type: 'date' },
        { name: 'exPracticalExamEndDate', label: 'Practical Exam End Date', icon: FaCalendarAlt, type: 'date' },
      ]
    },
    {
      title: '🏖️ Break & Semester Dates',
      fields: [
        { name: 'exSemesterBreakStartDate', label: 'Semester Break Start Date', icon: FaCalendarAlt, type: 'date' },
        { name: 'exSemesterBreakEndDate', label: 'Semester Break End Date', icon: FaCalendarAlt, type: 'date' },
        { name: 'exSemesterDetailsStartDate', label: 'Semester Start Date', icon: FaCalendarAlt, type: 'date' },
        { name: 'exSemesterDetailsEndDate', label: 'Semester End Date', icon: FaCalendarAlt, type: 'date' },
      ]
    }
  ], [degrees, programs, programYears, academicYears, semesters]);

  // Memoized table columns to prevent re-renders
  const tableColumns = useMemo(() => [
    { 
      key: 'exName', 
      label: 'Exam Details', 
      sortable: true,
      render: (item) => (
        <div>
          <div className="fw-bold text-primary">{item.exName}</div>
          {item.exLocalLangName && (
            <div className="text-muted small">{item.exLocalLangName}</div>
          )}
          {item.exDescription && (
            <div className="text-muted small mt-1">
              {item.exDescription.length > 60 ? 
                `${item.exDescription.substring(0, 60)}...` : 
                item.exDescription
              }
            </div>
          )}
        </div>
      )
    },
    { 
      key: 'academicInfo', 
      label: 'Academic Info',
      render: (item) => {
        const degree = (degrees || []).find(d => d.id === item.degree);
        const program = (programs || []).find(p => p.id === item.programmes);
        const programYear = (programYears || []).find(py => py.id === item.programmeyear);
        const academicYear = (academicYears || []).find(ay => ay.id === item.academicyear);
        const semester = (semesters || []).find(s => s.id === item.semester);
        
        return (
          <div className="small">
            <div><strong>Degree:</strong> {degree?.exName || 'N/A'}</div>
            <div><strong>Program:</strong> {program?.exName || 'N/A'}</div>
            <div><strong>Year:</strong> {programYear?.exName || 'N/A'}</div>
            <div><strong>AY:</strong> {academicYear?.exYear || 'N/A'}</div>
            <div><strong>Sem:</strong> {semester?.exName || 'N/A'}</div>
          </div>
        );
      }
    },
    { 
      key: 'examDates', 
      label: 'Exam Period',
      render: (item) => (
        <div className="small">
          {item.exExamDetailsStartDate && item.exExamDetailsEndDate ? (
            <div>
              <strong>Exam:</strong><br/>
              <small>
                {formatDateForDisplay(item.exExamDetailsStartDate)} to<br/>
                {formatDateForDisplay(item.exExamDetailsEndDate)}
              </small>
            </div>
          ) : (
            <span className="text-muted">Not set</span>
          )}
          {item.exPracticalExamStartDate && item.exPracticalExamEndDate && (
            <div className="mt-1">
              <strong>Practical:</strong><br/>
              <small>
                {formatDateForDisplay(item.exPracticalExamStartDate)} to<br/>
                {formatDateForDisplay(item.exPracticalExamEndDate)}
              </small>
            </div>
          )}
        </div>
      )
    },
    { 
      key: 'periods', 
      label: 'Duration',
      render: (item) => (
        <div className="small">
          {item.exExaminationPeriod && (
            <div><strong>Exam:</strong> {item.exExaminationPeriod}</div>
          )}
          {item.exInstructionalPeriod && (
            <div><strong>Instruction:</strong> {item.exInstructionalPeriod}</div>
          )}
          {item.exPracticalPeriod && (
            <div><strong>Practical:</strong> {item.exPracticalPeriod}</div>
          )}
          {!item.exExaminationPeriod && !item.exInstructionalPeriod && !item.exPracticalPeriod && (
            <span className="text-muted">Not specified</span>
          )}
        </div>
      )
    },
  ], [degrees, programs, programYears, academicYears, semesters]);

  // Enhanced CSV headers
  const csvHeaders = useMemo(() => [
    { key: 'exName', label: 'Exam Name' },
    { key: 'exLocalLangName', label: 'Local Language Name' },
    { key: 'exDescription', label: 'Description' },
    { key: 'degree', label: 'Degree ID' },
    { key: 'programmes', label: 'Program ID' },
    { key: 'programmeyear', label: 'Program Year ID' },
    { key: 'academicyear', label: 'Academic Year ID' },
    { key: 'semester', label: 'Semester ID' },
    { key: 'exExaminationPeriod', label: 'Examination Period' },
    { key: 'exInstructionalPeriod', label: 'Instructional Period' },
    { key: 'exPracticalPeriod', label: 'Practical Period' },
    { key: 'exExamDetailsStartDate', label: 'Exam Start Date' },
    { key: 'exExamDetailsEndDate', label: 'Exam End Date' },
    { key: 'exPracticalExamStartDate', label: 'Practical Start Date' },
    { key: 'exPracticalExamEndDate', label: 'Practical End Date' },
    { key: 'exIsLive', label: 'Status', render: (item) => item.exIsLive ? 'Live' : 'Draft' }
  ], []);

  // Enhanced view fields for modal
  const getViewFields = useCallback((item) => {
    if (!item) return [];
    
    const degree = (degrees || []).find(d => d.id === item.degree);
    const program = (programs || []).find(p => p.id === item.programmes);
    const programYear = (programYears || []).find(py => py.id === item.programmeyear);
    const academicYear = (academicYears || []).find(ay => ay.id === item.academicyear);
    const semester = (semesters || []).find(s => s.id === item.semester);
    
    return [
      // Basic Information
      { label: 'Exam Name', value: item.exName, icon: FaClipboardList },
      { label: 'Local Language Name', value: item.exLocalLangName, icon: FaGlobe },
      { label: 'Description', value: item.exDescription, icon: FaFileAlt, fullWidth: true },
      
      // Academic Information
      { label: 'Degree', value: degree?.exName, icon: FaGraduationCap },
      { label: 'Program', value: program?.exName, icon: FaBookOpen },
      { label: 'Program Year', value: programYear?.exName, icon: FaSchool },
      { label: 'Academic Year', value: academicYear?.exYear, icon: FaCalendarCheck },
      { label: 'Semester', value: semester?.exName, icon: FaBook },

      // Exam Configuration
      { label: 'Examination Period', value: item.exExaminationPeriod, icon: FaClock },
      { label: 'Instructional Period', value: item.exInstructionalPeriod, icon: FaClock },
      { label: 'Practical Period', value: item.exPracticalPeriod, icon: FaClock },

      // Exam Dates
      { label: 'Exam Start Date', value: formatDateForDisplay(item.exExamDetailsStartDate), icon: FaCalendarAlt },
      { label: 'Exam End Date', value: formatDateForDisplay(item.exExamDetailsEndDate), icon: FaCalendarAlt },
      { label: 'Practical Start Date', value: formatDateForDisplay(item.exPracticalExamStartDate), icon: FaCalendarAlt },
      { label: 'Practical End Date', value: formatDateForDisplay(item.exPracticalExamEndDate), icon: FaCalendarAlt },

      // Break & Semester Dates
      { label: 'Semester Break Start', value: formatDateForDisplay(item.exSemesterBreakStartDate), icon: FaCalendarAlt },
      { label: 'Semester Break End', value: formatDateForDisplay(item.exSemesterBreakEndDate), icon: FaCalendarAlt },
      { label: 'Semester Start Date', value: formatDateForDisplay(item.exSemesterDetailsStartDate), icon: FaCalendarAlt },
      { label: 'Semester End Date', value: formatDateForDisplay(item.exSemesterDetailsEndDate), icon: FaCalendarAlt },

      // Status
      { label: 'Publication Status', value: item.exIsLive ? 'Live (Published)' : 'Draft (Unpublished)', icon: FaEye, 
        className: item.exIsLive ? 'text-success fw-bold' : 'text-warning fw-bold' },
    ];
  }, [degrees, programs, programYears, academicYears, semesters]);

  // Handle search by exam name
  const handleSearchByName = useCallback(() => {
    if (!searchExamName.trim()) {
      toast.error("Please enter an exam name to search.");
      return;
    }
    
    // This will be handled by the GenericCrudPage's built-in search functionality
    // We just need to update the search query
  }, [searchExamName]);

  // Handle clear filters
  const handleClearFilters = useCallback(() => {
    setFilterDegree('');
    setSearchExamName('');
  }, []);

  // Custom action for toggling live status
  const toggleExamStatus = useCallback(async (exam) => {
    const action = exam.exIsLive ? "make draft" : "make live";
    const confirmMessage = `Are you sure you want to ${action} "${exam.exName}"?`;
    
    if (!window.confirm(confirmMessage)) return;

    try {
      const token = Cookies.get('token');
      const payload = {
        id: exam.id,
        exIsLive: exam.exIsLive ? 0 : 1,
        user: Number(Cookies.get("userId") || 1),
      };

      await axios.post(`${BASE_URL}/academic/updateExamAnnouncement`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Exam ${exam.exIsLive ? 'moved to draft' : 'published'} successfully`);
      
      // Refresh the data
      window.location.reload(); // Simple refresh - you can implement a more elegant solution
    } catch (err) {
      console.error("Toggle failed", err);
      const errorMessage = err.response?.data?.message || "Failed to update status";
      toast.error(errorMessage);
    }
  }, []);

  // Custom action buttons for exam-specific operations
  const customActions = useMemo(() => [
    {
      label: (item) => item.exIsLive ? 'Make Draft' : 'Make Live',
      icon: (item) => item.exIsLive ? '📝' : '🌐',
      variant: (item) => item.exIsLive ? 'outline-warning' : 'outline-success',
      onClick: toggleExamStatus,
      title: (item) => item.exIsLive ? 'Move to draft' : 'Publish exam'
    }
  ], [toggleExamStatus]);

  return (
    <Container fluid className="py-4">
      {dropdownsLoading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading dropdown data...</span>
          </div>
          <p className="mt-3 text-muted">Loading form data...</p>
        </div>
      ) : (
        <>
          {/* Enhanced Statistics Section */}
          <Row className="mb-4">
            <Col lg={3} md={6} className="mb-3">
              <div className="bg-primary text-white rounded p-3 h-100">
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <h6 className="mb-1 opacity-75">Total Exams</h6>
                    <h3 className="mb-0 fw-bold">{examStats.total}</h3>
                  </div>
                  <FaClipboardList size={32} className="opacity-75" />
                </div>
              </div>
            </Col>
            <Col lg={3} md={6} className="mb-3">
              <div className="bg-success text-white rounded p-3 h-100">
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <h6 className="mb-1 opacity-75">Live Exams</h6>
                    <h3 className="mb-0 fw-bold">{examStats.live}</h3>
                  </div>
                  <FaEye size={32} className="opacity-75" />
                </div>
              </div>
            </Col>
            <Col lg={3} md={6} className="mb-3">
              <div className="bg-warning text-dark rounded p-3 h-100">
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <h6 className="mb-1 opacity-75">Draft Exams</h6>
                    <h3 className="mb-0 fw-bold">{examStats.draft}</h3>
                  </div>
                  <FaEdit size={32} className="opacity-75" />
                </div>
              </div>
            </Col>
            <Col lg={3} md={6} className="mb-3">
              <div className="bg-info text-white rounded p-3 h-100">
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <h6 className="mb-1 opacity-75">Active Filter</h6>
                    <p className="mb-0 fw-semibold small">
                      {filterDegree 
                        ? `${degrees?.find(d => d.id == filterDegree)?.exName || 'Unknown'}` 
                        : 'All Degrees'
                      }
                    </p>
                  </div>
                  <FaChartBar size={32} className="opacity-75" />
                </div>
              </div>
            </Col>
          </Row>

          {/* Enhanced Filter Section */}
          <Row className="mb-4 align-items-end">
            <Col md={4}>
              <Form.Label className="fw-semibold">
                <FaGraduationCap className="me-2" />
                Filter by Degree
              </Form.Label>
              <Form.Select
                value={filterDegree}
                onChange={(e) => {
                  const newDegree = e.target.value;
                  setFilterDegree(newDegree);
                  setSearchExamName('');
                }}
                className="form-select-lg"
              >
                <option value="">🎓 All Degrees</option>
                {(Array.isArray(degrees) ? degrees : [])
                  .filter(Boolean)
                  .map((d) => (
                    <option key={d.id} value={d.id}>{d.exName}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={5}>
              <Form.Label className="fw-semibold">
                <FaSearch className="me-2" />
                Search by Exam Name
              </Form.Label>
              <InputGroup size="lg">
                <FormControl
                  placeholder="Enter exam name to search..."
                  value={searchExamName}
                  onChange={(e) => setSearchExamName(e.target.value)}
                  onKeyPress={(e) => { if (e.key === 'Enter') handleSearchByName(); }}
                />
                <Button 
                  variant="outline-primary" 
                  onClick={handleSearchByName}
                  disabled={!searchExamName.trim()}
                >
                  <FaSearch /> Search
                </Button>
              </InputGroup>
            </Col>
            <Col md={3} className="d-flex justify-content-start">
              <Button 
                variant="outline-secondary" 
                onClick={handleClearFilters}
                size="lg"
                className="d-flex align-items-center"
              >
                <span className="me-2">🔄</span>
                Clear Filters
              </Button>
            </Col>
          </Row>

          {/* Quick Actions & Info Panel */}
          <Row className="mb-4">
            <Col>
              <Alert variant="info" className="mb-0">
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <strong>📋 Exam Management Tips:</strong>
                    <ul className="mb-0 mt-2 small">
                      <li>Academic information (Degree, Program, etc.) cannot be changed after exam creation</li>
                      <li>Use the "Make Live/Draft" button to control exam visibility</li>
                      <li>All date fields are optional but end dates must be after start dates</li>
                      <li>Export data to CSV for reporting and analysis</li>
                    </ul>
                  </div>
                  <div className="text-end">
                    <FaAward size={32} className="text-primary opacity-75" />
                  </div>
                </div>
              </Alert>
            </Col>
          </Row>

          <GenericCrudPage
            key={`exam-crud-${filterDegree}`} // Use filterDegree as key instead of fetchConfig
            title="Examination"
            subtitle="Manage examination schedules and announcements"
            config={config}
            formFields={formFields}
            tableColumns={tableColumns}
            emptyIcon={FaCalendarAlt}
            emptyTitle="No Exams Found"
            emptyMessage="Get started by creating your first exam announcement"
            searchQuery={searchExamName}
            setSearchQuery={setSearchExamName}
            csvHeaders={csvHeaders}
            csvFilename="exam_announcements.csv"
            transformPayload={transformPayload}
            transformToForm={transformToForm}
            getViewFields={getViewFields}
            fetchItemForView={fetchExamById}
            fetchItemForEdit={fetchExamById}
            customActions={customActions}
            showQuickAdd={true}
            modalSize="xl"
            tableSize="lg"
            cardClassName="shadow-sm"
          />

          <GenericViewModal
            show={showExamViewModal}
            onHide={() => setShowExamViewModal(false)}
            item={selectedExam}
            title="Exam Details"
            fields={getViewFields(selectedExam)}
          />
        </>
      )}
    </Container>
  );
};

export default Examination;